import { useContext, useMemo, useState } from "react";
import {
	Button,
	Container,
	Dropdown,
	Image,
	Modal,
	Nav,
	Navbar,
} from "react-bootstrap";
import { useAuth } from "react-oidc-context";
import { useIsFetching, useIsMutating } from "react-query";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import {
	faCog,
	faEnvelope,
	faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AustinLogo from "../../assets/img/austinlogo.svg";
import { LAST_ROUTE, blastRoutes, routes } from "../../constants";
import { RouteContext } from "../../contexts/routeContext";
import { SiteContext } from "../../contexts/siteContext";
import { UserDataContext } from "../../contexts/userDataContext";
import {
	WEBTITLE,
	getBlastUiOrigin,
	getCrmUiOrigin,
	getIsSysAdmin,
	getServiceOrigin,
	isBlast,
	isRoutePermitted,
	isTech,
	orgPath,
} from "../../utils";
import { OrgAvatarAndText } from "../avatarAndText";
import { PermissionsModal } from "./permissionsModal";
import { isBlockedOrg } from "./sideNavSection";
import { TopCrumb } from "./topCrumb";

const DropdownNavItem = ({ route, icon, text }) => {
	const { isAuthenticated } = useAuth();
	const { selectedOrg } = useContext(UserDataContext);
	const { selectedSite } = useContext(SiteContext);

	const isSysAdmin = useMemo(getIsSysAdmin, [isAuthenticated]);

	if (
		!isRoutePermitted(
			{
				...selectedOrg.permissions,
				...selectedSite.permissions,
			},
			route.permissionsm,
			isSysAdmin
		)
	) {
		return null;
	}

	return (
		<Dropdown.Item as={Link} to={orgPath(selectedOrg?.id, route.path)}>
			<FontAwesomeIcon icon={icon} className="me-2" />
			{text}
		</Dropdown.Item>
	);
};

export const TestIndicator = () => {
	// Throw during render (not in the click handler) — React error boundaries
	// only catch render/lifecycle errors, so an onClick throw would escape the
	// ErrorBoundary and never exercise componentDidCatch / the route tag.
	const [crash, setCrash] = useState(false);
	if (crash) throw new Error("This is your first error!");

	const crmOrigin = getServiceOrigin("crm");
	const testText = crmOrigin.includes("localhost")
		? "LOCAL"
		: crmOrigin.includes("test")
		? "TEST"
		: null;

	if (testText === null) return null;

	return (
		<h3
			onClick={() => setCrash(true)}
			className="me-3"
			style={{ color: "fuchsia" }}
		>
			{testText}
		</h3>
	);
};

const OrganizationModal = ({
	isOrgModalShowing,
	setOrgModalShow,
	orgToSwitchTo,
}) => {
	const { route } = useContext(RouteContext);

	const handleClose = () => {
		setOrgModalShow(false);
	};

	const handleSubmit = () => {
		window.location = `/org/${orgToSwitchTo.id}${route.path}`;
	};

	return (
		<Modal
			as={Modal.Dialog}
			centered
			show={isOrgModalShowing}
			onHide={handleClose}
		>
			<Modal.Header>
				<Modal.Title className="h6">Change Organization</Modal.Title>
				<Button
					variant="close"
					aria-label="Close"
					onClick={handleClose}
				/>
			</Modal.Header>
			<Modal.Body>
				Are you sure you wish to change organisation to:{" "}
				{orgToSwitchTo?.name}
			</Modal.Body>
			<Modal.Footer>
				<Button
					variant="link"
					className="btn-success ms-auto"
					onClick={handleSubmit}
				>
					Yes
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export const TopNavbar = ({ setShow = (_show) => {} }) => {
	const [isOrgModalShowing, setOrgModalShow] = useState(false);
	const [orgToSwitchTo, setOrgToSwitch] = useState(null);

	const [isPermissionsModalShowing, setPermissionsModalShow] =
		useState(false);

	const isSysadmin = useMemo(getIsSysAdmin, []);

	const { loaderCount } = useSelector((state) => state.app);

	const { selectedOrg, setNewSelectedOrg, userOrgs, userProfile } =
		useContext(UserDataContext);
	const { selectedSite } = useContext(SiteContext);

	const { isAuthenticated } = useAuth();
	const isSysAdmin = useMemo(getIsSysAdmin, [isAuthenticated]);

	const isFetching = useIsFetching() + useIsMutating();

	const { signoutRedirect, user: authUser } = useAuth();

	const userLogout = () => {
		sessionStorage.setItem(LAST_ROUTE, window.location.pathname);

		signoutRedirect({
			id_token_hint: authUser?.id_token,
			state: authUser?.state,
		});
	};

	const handleOrgClick = (org) => () => {
		setOrgToSwitch(org);
		setOrgModalShow(true);
	};

	const orgSettingsBtn = useMemo(
		() =>
			isRoutePermitted(
				{
					...selectedOrg.permissions,
					...selectedSite.permissions,
				},
				routes.orgEditor.permissions,
				isSysAdmin
			) && !isBlast ? (
				<DropdownNavItem
					route={routes.orgEditor}
					text="Org Settings"
					icon={faCog}
				/>
			) : null,
		[selectedOrg.id, isSysAdmin]
	);

	return (
		<>
			<PermissionsModal
				setModalShow={setPermissionsModalShow}
				isModalShow={isPermissionsModalShowing}
			/>
			<OrganizationModal
				{...{
					isOrgModalShowing,
					setOrgModalShow,
					setNewSelectedOrg,
					orgToSwitchTo,
				}}
			/>
			<Navbar
				variant="dark"
				expanded
				style={{ zIndex: 1030 }}
				className="ps-0 pe-2 pb-0 pt-1 bg-charcoal text-gray position-fixed w-100 top-0"
			>
				<Container fluid className="px-0">
					<div className="d-flex justify-content-between w-100">
						<div className="ms-3 mt-2 d-flex flex-row p-0">
							<Nav.Item className="pt-1">
								<Nav.Link
									className="d-none d-md-block"
									style={{ width: "145px" }}
									to={orgPath(
										selectedOrg?.id,
										routes.presentation.path
									)}
									as={Link}
								>
									<Image
										src={AustinLogo}
										width={50}
										height={29}
										className="svg-icon me-2"
									/>
									<span className="text-gray-300">
										{WEBTITLE}
									</span>
								</Nav.Link>
								<Nav.Link
									onClick={() => setShow((show) => !show)}
									className="d-block d-md-none"
								>
									<Image
										src={AustinLogo}
										width={50}
										height={29}
										className="svg-icon me-2"
									/>
									<span className="text-gray-300">Menu</span>
								</Nav.Link>
							</Nav.Item>
							<TopCrumb />
						</div>
						<TestIndicator />
						<Nav
							variant="tabs"
							className="ms-auto me-5 flex-row small text-gray-400 "
						>
							{selectedOrg?.id !==
								"221c3f15-1e84-431a-1974-08daf8442809" &&
							!isBlockedOrg(selectedOrg) ? (
								<>
									<Nav.Item>
										<Nav.Link
											href={
												getCrmUiOrigin() +
												"/org/" +
												selectedOrg.id
											}
											className={`${
												isBlast ? "" : "active"
											} p-2 mt-3`}
										>
											Cloud Portal
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link
											className={`${
												isBlast ? "active" : ""
											} p-2 mt-3`}
											href={
												getBlastUiOrigin() +
												"/org/" +
												selectedOrg.id
											}
										>
											Blast Data
										</Nav.Link>
									</Nav.Item>
								</>
							) : null}
						</Nav>
						<Nav className="align-items-center">
							{loaderCount > 0 || isFetching ? (
								<div className="spinning-loader me-2" />
							) : (
								<div
									style={{ width: "25px" }}
									className="me-2"
								></div>
							)}
							<Dropdown as={Nav.Item}>
								<Dropdown.Toggle
									as={Nav.Link}
									className="pt-1 px-0"
								>
									<div className="media d-flex align-items-center">
										<Image
											src={userProfile.avatar_Base64}
											className="user-avatar md-avatar rounded-circle"
										/>
										<div className="media-body ms-2 align-items-center d-none d-lg-block me-3">
											<span className="mb-0 font-small text-white fw-bold">
												{userProfile.firstName}{" "}
												{userProfile.lastName} -{" "}
												{selectedOrg.name}
											</span>
										</div>
									</div>
								</Dropdown.Toggle>
								<Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2">
									<Dropdown.Item
										key={"My Profile"}
										onClick={() =>
											(window.location =
												(isTech
													? import.meta.env
															.VITE_STS_AUTHORITY_TECH
													: import.meta.env
															.VITE_STS_AUTHORITY) +
												routes.userProfile.path)
										}
									>
										<FontAwesomeIcon
											icon={faUserCircle}
											className="me-2"
										/>
										My Profile
									</Dropdown.Item>
									{isBlast ? null : (
										<Dropdown.Item
											as={Link}
											to={
												routes.emailPrefs.path
													.replace(
														":email",
														encodeURIComponent(
															userProfile.email
														)
													)
													.replace(
														":code",
														encodeURIComponent(
															userProfile.id
														)
													) +
												"?backUrl=" +
												encodeURIComponent(
													window.location.href
												)
											}
										>
											<FontAwesomeIcon
												icon={faEnvelope}
												className="me-2"
											/>
											My Email Preferences
										</Dropdown.Item>
									)}
									{orgSettingsBtn}
									{isSysadmin ? (
										<Dropdown.Item
											key={`mock-perms`}
											onClick={() =>
												setPermissionsModalShow(true)
											}
										>
											Mock Permissions
										</Dropdown.Item>
									) : null}
									<Dropdown.Divider />
									<Dropdown.Item className="fw-bold">
										<OrgAvatarAndText org={selectedOrg} />
									</Dropdown.Item>
									{userOrgs
										.filter(
											(org) => org.id !== selectedOrg.id
										)
										.sort((a, b) =>
											a.name.localeCompare(b.name)
										)
										.map((org) => (
											<Dropdown.Item
												key={`org-${org.id}`}
												style={{ paddingLeft: "25px" }}
												onClick={handleOrgClick(org)}
											>
												<OrgAvatarAndText org={org} />
											</Dropdown.Item>
										))}
									<Dropdown.Divider />
									<Dropdown.Item
										className="fw-bold"
										onClick={userLogout}
									>
										<FontAwesomeIcon
											icon={faSignOutAlt}
											className="text-danger me-2"
										/>
										Logout
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Nav>
					</div>
				</Container>
			</Navbar>
		</>
	);
};
